import { Form, InputGroup } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Footbal from "./../../../assets/img/giftshop/landing/American-Football.png";
import Search from "../../../compoment/icon/Search";
import { Search as SearchNotFound, Grid, Star, X, Check } from "react-feather";
import { Back } from "../../../compoment/icon";
import ExitModal from "../../../modal/ExitModal";
import SelectHobbyConfirmModal from "../../../modal/SelectHobbyConfirmModal";
import SignOutConfirmModal from "../../../modal/SignOutConfirmModal";
import {
  clearHobbyPersonHobbySelections,
  type HobbyPersonHobbySelection,
} from "../../../redux/slices/uploadFlowSlice";
import {
  isAiSelfieCaricatureHobbyPostPaymentActive,
  resolveHobbyCaricatureRouteSlug,
  shouldUseHobbyCaricatureFlowForStoredAiSelfieArt,
} from "../../../services/stylePromptResolver";
import type { RootState } from "../../../redux/store";
import { saveHobbyPersonHobbySelection } from "../../../utils/hobbyPersonFlow";
import {
  persistStationSelection,
  SELECTED_CARICATURE_HOBBIES_STORAGE_KEY,
} from "../../../utils/stationFlowStorage";
import {
  CaricatureHobby,
  CaricatureHobbyCategory,
  categoryImageUrl,
  getActiveCaricatureHobbyCategories,
  getCaricatureHobbiesByCategory,
  getPopularCaricatureHobbies,
  hobbyImageUrl,
  incrementCaricatureHobbyUsage,
  searchCaricatureHobbies,
} from "../../../services/caricatureHobbyService";
import Loader from "../../../compoment/ui/Loader";
import StationThemeLayout from "../../../layouts/StationThemeLayout";

const SELECTED_HOBBIES_STORAGE_KEY = SELECTED_CARICATURE_HOBBIES_STORAGE_KEY;
const ALL_CATEGORY_ID = "all";
const POPULAR_CATEGORY_ID = "popular";

const POPULAR_CATEGORY: CaricatureHobbyCategory = {
  id: POPULAR_CATEGORY_ID,
  name: "Popular",
  description: null,
  status: true,
  icon: null,
  icon_url: null,
  created_at: "",
  updated_at: "",
};

const TagCategoryIcon = ({ src }: { src: string }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [src]);

  if (failed) return null;

  return (
    <span className={`tagIconWrap${loaded ? " is-loaded" : ""}`}>
      {!loaded && <span className="tagIconShimmer" aria-hidden="true" />}
      <img
        className="tagIcon"
        src={src}
        alt=""
        aria-hidden
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
      />
    </span>
  );
};

const SelectHobby = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const dispatch = useDispatch();
  const aiSelfiePostPaymentHobby = isAiSelfieCaricatureHobbyPostPaymentActive();
  const caricatureHobbyByArt = shouldUseHobbyCaricatureFlowForStoredAiSelfieArt();
  const {
    hobbyPersonImages,
    hobbyPeopleCount,
    hobbyPersonHobbySelections,
    hobbyPersonCompletedResults,
    hobbyGroupPhotoFlow,
  } = useSelector((s: RootState) => s.uploadFlow);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [showBackConfirmModal, setShowBackConfirmModal] = useState(false);
  const [pendingHobby, setPendingHobby] = useState<CaricatureHobby | null>(null);
  const [showHobbyConfirmModal, setShowHobbyConfirmModal] = useState(false);
  /** Which person slot is being assigned a hobby in group-batch mode. */
  const [groupSelectingIndex, setGroupSelectingIndex] = useState(0);
  const [categories, setCategories] = useState<CaricatureHobbyCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(POPULAR_CATEGORY_ID);
  const [hobbies, setHobbies] = useState<CaricatureHobby[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [hobbiesLoading, setHobbiesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [hobbiesError, setHobbiesError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState<CaricatureHobby[]>([]);
  const [searchCategoryCounts, setSearchCategoryCounts] = useState<Record<string, number>>({});

  const stationData = useMemo(() => {
    const selectedRaw = localStorage.getItem("selectedStation");
    const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
    const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
    return fromSelected || fromStorage;
  }, [location.state]);

  // Prefer current URL slug (e.g. hobby-caricature), not parent category type
  // (caricature-cartoon) that can live on stationData.
  const routeSlug = useMemo(
    () => resolveHobbyCaricatureRouteSlug(slug),
    [slug]
  );

  const totalPeople = hobbyPeopleCount ?? 1;
  const isGroupBatchFlow = Boolean(hobbyGroupPhotoFlow) && totalPeople > 1;

  const currentPersonIndex = isGroupBatchFlow
    ? Math.min(Math.max(0, groupSelectingIndex), Math.max(0, totalPeople - 1))
    : hobbyPersonCompletedResults.length;
  const currentPersonNumber = currentPersonIndex + 1;
  const groupSelectionsComplete =
    isGroupBatchFlow &&
    Array.from({ length: totalPeople }, (_, i) => hobbyPersonHobbySelections[i]?.id).every(Boolean);

  useEffect(() => {
    if (hobbyPersonHobbySelections.length > totalPeople) {
      dispatch(clearHobbyPersonHobbySelections());
    }
  }, [dispatch, hobbyPersonHobbySelections.length, totalPeople]);

  useEffect(() => {
    if (!isGroupBatchFlow) return;
    // Prefer the first person who still needs a hobby.
    const nextEmpty = Array.from({ length: totalPeople }, (_, i) => i).find(
      (i) => !hobbyPersonHobbySelections[i]?.id
    );
    if (typeof nextEmpty === "number") {
      setGroupSelectingIndex(nextEmpty);
    }
  }, [isGroupBatchFlow, totalPeople, hobbyPersonHobbySelections]);

  const isHobbyCaricatureFlow =
    stationData?.type === "hobby-caricature" ||
    aiSelfiePostPaymentHobby ||
    caricatureHobbyByArt;

  const requiredPhotos = isGroupBatchFlow ? totalPeople : currentPersonIndex + 1;

  useEffect(() => {
    if (!isHobbyCaricatureFlow) return;
    if (hobbyPersonImages.length < requiredPhotos) {
      navigate(`/${routeSlug}/select-media-library`, {
        replace: true,
      });
    }
  }, [isHobbyCaricatureFlow, hobbyPersonImages.length, navigate, requiredPhotos, currentPersonIndex, routeSlug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCategoriesLoading(true);
      setCategoriesError(null);
      const res = await getActiveCaricatureHobbyCategories();
      if (cancelled) return;
      if (!res.success && res.data.length === 0) {
        setCategoriesError(res.error || "Could not load categories");
        setCategories([]);
        setSelectedCategoryId(POPULAR_CATEGORY_ID);
      } else {
        setCategories(res.data);
        setSelectedCategoryId((prev) => {
          if (prev === POPULAR_CATEGORY_ID || prev === ALL_CATEGORY_ID) return prev;
          if (prev && res.data.some((c) => c.id === prev)) return prev;
          return POPULAR_CATEGORY_ID;
        });
      }
      setCategoriesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isSearchActive) return;
    if (!selectedCategoryId || selectedCategoryId === ALL_CATEGORY_ID) {
      setHobbies([]);
      if (!categoriesLoading) setHobbiesLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setHobbiesLoading(true);
      setHobbiesError(null);
      const res =
        selectedCategoryId === POPULAR_CATEGORY_ID
          ? await getPopularCaricatureHobbies()
          : await getCaricatureHobbiesByCategory(selectedCategoryId);
      if (cancelled) return;
      if (!res.success && res.data.length === 0) {
        setHobbiesError(
          res.error ||
            (selectedCategoryId === POPULAR_CATEGORY_ID
              ? "Could not load popular hobbies"
              : "Could not load hobbies")
        );
        setHobbies([]);
      } else {
        setHobbies(res.data);
      }
      setHobbiesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCategoryId, isSearchActive, categoriesLoading]);

  const displayedHobbies = useMemo(() => {
    if (!isSearchActive) return hobbies;
    if (!selectedCategoryId || selectedCategoryId === ALL_CATEGORY_ID) return searchResults;
    return searchResults.filter((hobby) => hobby.category_id === selectedCategoryId);
  }, [isSearchActive, hobbies, searchResults, selectedCategoryId]);

  const isHobbyListLoading = categoriesLoading || hobbiesLoading;
  const showEmptyList =
    !isHobbyListLoading && !hobbiesError && displayedHobbies.length === 0;

  const categoryRows = useMemo(() => {
    const tags: CaricatureHobbyCategory[] = isSearchActive
      ? [
          {
            id: ALL_CATEGORY_ID,
            name: "All",
            description: null,
            status: true,
            icon: null,
            icon_url: null,
            created_at: "",
            updated_at: "",
          } as CaricatureHobbyCategory,
          ...categories,
        ]
      : [POPULAR_CATEGORY, ...categories];
    const midpoint = Math.ceil(tags.length / 2) || 1;
    return [tags.slice(0, midpoint), tags.slice(midpoint)];
  }, [categories, isSearchActive]);

  const renderCategoryTag = (cat: CaricatureHobbyCategory) => {
    const isAllTag = cat.id === ALL_CATEGORY_ID;
    const isPopularTag = cat.id === POPULAR_CATEGORY_ID;
    const catIconSrc = isAllTag || isPopularTag ? "" : categoryImageUrl(cat);
    const badgeCount = isSearchActive
      ? isAllTag
        ? searchResults.length
        : searchCategoryCounts[cat.id] ?? 0
      : typeof cat.items_count === "number"
        ? cat.items_count
        : undefined;
    return (
      <div
        key={cat.id}
        className={`tag${isAllTag || isPopularTag || catIconSrc ? " tag--withIcon" : ""} ${selectedCategoryId === cat.id ? "selected" : ""
          }`}
        onClick={() => {
          setSelectedCategoryId(cat.id);
          if (!isSearchActive) setHobbiesLoading(true);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setSelectedCategoryId(cat.id);
            if (!isSearchActive) setHobbiesLoading(true);
          }
        }}
      >
        {isPopularTag ? (
          <span className="tagIconWrap is-loaded" aria-hidden="true">
            <Star
              className="tagIconSvg tagIconSvg--popular"
              size={18}
              strokeWidth={2}
              color="#F5C518"
              fill="#F5C518"
            />
          </span>
        ) : isAllTag ? (
          <span className="tagIconWrap is-loaded" aria-hidden="true">
            <Grid className="tagIconSvg" size={18} strokeWidth={2} />
          </span>
        ) : catIconSrc ? (
          <TagCategoryIcon src={catIconSrc} />
        ) : null}
        <span className="tagLabel">{cat.name}</span>
        {typeof badgeCount === "number" && (
          <span className="tagCountBadge">{badgeCount}</span>
        )}
      </div>
    );
  };

  const handleExit = () => {
    navigate(`/${routeSlug}/idle`);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setIsSearchActive(false);
    setSearchResults([]);
    setSearchCategoryCounts({});
    setHobbiesError(null);
    setHobbiesLoading(true);
    setSelectedCategoryId(POPULAR_CATEGORY_ID);
  }, []);

  const handleSearch = useCallback(async () => {
    const term = searchTerm.trim();
    if (!term) {
      handleClearSearch();
      return;
    }

    setIsSearchActive(true);
    setSelectedCategoryId(ALL_CATEGORY_ID);
    setHobbiesLoading(true);
    setHobbiesError(null);
    const res = await searchCaricatureHobbies(term);
    if (!res.success && res.data.length === 0) {
      setHobbiesError(res.error || "Could not search hobbies");
      setSearchResults([]);
      setSearchCategoryCounts({});
    } else {
      setSearchResults(res.data);
      setSearchCategoryCounts(res.categoryCounts);
    }
    setHobbiesLoading(false);
  }, [searchTerm, handleClearSearch]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleHobbyClick = (hobby: CaricatureHobby) => {
    setPendingHobby(hobby);
    setShowHobbyConfirmModal(true);
  };

  const handleHobbyConfirmNo = () => {
    setShowHobbyConfirmModal(false);
    setPendingHobby(null);
  };

  const handleHobbyConfirmYes = async () => {
    if (!pendingHobby) return;
    const hobby = pendingHobby;
    setShowHobbyConfirmModal(false);
    setPendingHobby(null);
    await selectHobbyAndContinue(hobby);
  };

  const selectHobbyAndContinue = async (hobby: CaricatureHobby) => {
    await incrementCaricatureHobbyUsage(hobby.id, hobby.category_id);

    const selection: HobbyPersonHobbySelection = {
      id: hobby.id,
      name: hobby.name,
      category_id: hobby.category_id,
      icon_url: hobbyImageUrl(hobby),
    };
    const personIndex = isGroupBatchFlow
      ? currentPersonIndex
      : hobbyPersonCompletedResults.length;
    const allSelections = saveHobbyPersonHobbySelection(personIndex, selection);
    try {
      localStorage.setItem(SELECTED_HOBBIES_STORAGE_KEY, JSON.stringify(allSelections));
    } catch {
      /* ignore quota errors */
    }

    const merged = { ...stationData };
    persistStationSelection(merged);

    // Group photo: assign hobbies for every person first, then generate once.
    if (isGroupBatchFlow) {
      const allFilled = Array.from(
        { length: totalPeople },
        (_, i) => allSelections[i]?.id
      ).every(Boolean);
      if (!allFilled) {
        const nextEmpty = Array.from({ length: totalPeople }, (_, i) => i).find(
          (i) => i !== personIndex && !allSelections[i]?.id
        );
        setGroupSelectingIndex(
          typeof nextEmpty === "number" ? nextEmpty : Math.min(personIndex + 1, totalPeople - 1)
        );
        return;
      }

      navigate(`/${routeSlug}/creating-person-hobby`, {
        state: {
          selectedHobbies: allSelections,
          stationData: merged,
          batchGroupGeneration: true,
        },
      });
      return;
    }

    // Temporarily skip hobby-write-line; keep that route for a future release.
    navigate(`/${routeSlug}/creating-person-hobby`, {
      state: {
        selectedHobbies: allSelections,
        selectedHobby: hobby,
        stationData: merged,
        currentPersonIndex: personIndex,
      },
    });
  };

  const startGroupBatchGeneration = () => {
    if (!groupSelectionsComplete) return;
    const merged = { ...stationData };
    persistStationSelection(merged);
    navigate(`/${routeSlug}/creating-person-hobby`, {
      state: {
        selectedHobbies: hobbyPersonHobbySelections,
        stationData: merged,
        batchGroupGeneration: true,
      },
    });
  };

  return (
    <>
      <StationThemeLayout
        className="mainSection selectHobbyThemePage p-0"
        station={stationData}
        showStationLogo
        onBack={() => setShowBackConfirmModal(true)}
      >
        {/* <button
          className="backBTN chooseOptionBack border-0"
          aria-label="Back"
          onClick={() =>
            aiSelfiePostPaymentHobby || caricatureHobbyByArt
              ? navigate("/hobby-caricature/select-media-library")
              : navigate(-1)
          }
        >
          <Back />
        </button> */}

        <div className="selectBrand">
          <div className="heading">
            {isGroupBatchFlow ? (
              <>
                Choose a <span>hobby</span> for person {currentPersonNumber} of {totalPeople}
              </>
            ) : (
              <>
                Choose a <span>category</span>, then a <span>hobby</span> — person{" "}
                {currentPersonNumber} of {totalPeople}
              </>
            )}
          </div>

          {isGroupBatchFlow ? (
            <div className="groupHobbyPersonStrip" aria-label="People in group photo">
              {Array.from({ length: totalPeople }, (_, index) => {
                const selected = hobbyPersonHobbySelections[index];
                const thumb = hobbyPersonImages[index];
                const isActive = index === currentPersonIndex;
                return (
                  <button
                    key={`group-person-${index}`}
                    type="button"
                    className={`groupHobbyPersonChip${isActive ? " is-active" : ""}${
                      selected?.id ? " is-selected" : ""
                    }`}
                    onClick={() => setGroupSelectingIndex(index)}
                  >
                    <span className="groupHobbyPersonChipThumb">
                      {thumb ? <img src={thumb} alt="" /> : <span>{index + 1}</span>}
                    </span>
                    <span className="groupHobbyPersonChipMeta">
                      <span className="groupHobbyPersonChipLabel">Person {index + 1}</span>
                      <span className="groupHobbyPersonChipHobby">
                        {selected?.name || "Pick hobby"}
                      </span>
                    </span>
                    {selected?.id ? (
                      <span className="groupHobbyPersonChipCheck" aria-hidden="true">
                        <Check size={14} strokeWidth={3} />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : null}

          {isGroupBatchFlow && groupSelectionsComplete ? (
            <button
              type="button"
              className="groupHobbyCreateAllBtn"
              onClick={startGroupBatchGeneration}
            >
              Create All Caricatures
            </button>
          ) : null}

          <div
            className={`searchField mt-2${
              searchTerm.trim() || isSearchActive ? " has-clear" : ""
            }`}
          >
            <InputGroup className="mb-3">
              <Form.Control
                placeholder="Search a hobby..."
                aria-label="Search a hobby..."
                aria-describedby="basic-addon2"
                value={searchTerm}
                onChange={handleChange}
                onKeyDown={handleKeyPress}
              />
              {(searchTerm.trim() || isSearchActive) && (
                <button
                  type="button"
                  className="searchClearBtn"
                  aria-label="Clear search"
                  onClick={handleClearSearch}
                >
                  <X size={22} strokeWidth={2.25} />
                </button>
              )}
              <InputGroup.Text
                id="basic-addon2"
                style={{ cursor: "pointer" }}
                onClick={handleSearch}
              >
                <Search />
              </InputGroup.Text>
            </InputGroup>
          </div>
          <div className="tagList">
            {categoriesLoading ? (
              <Loader />
            ) : (
              <div className="tagListScroller">
                <div className="tagListRows">
                  {categoryRows.map((row, rowIndex) => (
                    <div key={rowIndex} className="tagListRow">
                      {row.map((cat) => renderCategoryTag(cat))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className={`brandList${showEmptyList ? " is-empty" : ""}`}>
            {isHobbyListLoading && (
              <div className="brandListStatus">
                <Loader />
              </div>
            )}
            {!isHobbyListLoading && hobbiesError && (
              <div className="brandListStatus text-danger">{hobbiesError}</div>
            )}

            {!isHobbyListLoading &&
              !hobbiesError &&
              displayedHobbies.map((hobby) => {
                const src = hobbyImageUrl(hobby);
                return (
                  <div
                    key={hobby.id}
                    className="singleBrand shadow-sm"
                    onClick={() => handleHobbyClick(hobby)}
                    title={hobby.name}
                  >
                    <img
                      src={src || Footbal}
                      alt={hobby.name}
                      onError={(e) => {
                        const el = e.currentTarget;
                        if (!el.dataset.fallbackApplied) {
                          el.dataset.fallbackApplied = "1";
                          el.src = Footbal;
                        }
                      }}
                    />
                    <span className="hobbyCardLabel">{hobby.name}</span>
                  </div>
                );
              })}

            {showEmptyList && (
                <div className="brandListEmpty">
                  <div className="brandListEmptyIcon" aria-hidden="true">
                    <SearchNotFound size={56} strokeWidth={1.75} />
                  </div>
                  <div className="brandListEmptyTitle">Not found</div>
                  <div className="brandListEmptyText">
                    {isSearchActive
                      ? "No hobbies match your search"
                      : selectedCategoryId === POPULAR_CATEGORY_ID
                        ? "No popular hobbies right now"
                        : selectedCategoryId && selectedCategoryId !== ALL_CATEGORY_ID
                          ? "No hobbies in this category"
                          : "Choose a category to see hobbies"}
                  </div>
                </div>
              )}
          </div>
        </div>
      </StationThemeLayout>

      {showBackConfirmModal && (
        <SignOutConfirmModal
          show={showBackConfirmModal}
          onHide={() => setShowBackConfirmModal(false)}
          onConfirm={() => {
            setShowBackConfirmModal(false);
            navigate("/");
          }}
          message="Are you sure you want to go back to the home screen?"
          cancelLabel="No"
          confirmLabel="Yes"
        />
      )}

      {showHobbyConfirmModal && pendingHobby && (
        <SelectHobbyConfirmModal
          show={showHobbyConfirmModal}
          onHide={handleHobbyConfirmNo}
          hobbyName={pendingHobby.name}
          hobbyImageUrl={hobbyImageUrl(pendingHobby)}
          fallbackImage={Footbal}
          isLandscapeArt
          personNumber={isGroupBatchFlow ? currentPersonNumber : undefined}
          onConfirm={handleHobbyConfirmYes}
        />
      )}

      {confirmationModal && (
        <ExitModal
          show={confirmationModal}
          onHide={() => setConfirmationModal(false)}
          handleSubmit={handleExit}
        />
      )}
    </>
  );
};

export default SelectHobby;
